import { FC } from 'react'
import {
	Box,
	Button,
	Container,
	ContentLayout,
	Header,
	SpaceBetween,
	StatusIndicator,
	Table
} from '@cloudscape-design/components'
import { useNavigate, useParams } from 'react-router-dom'

import useMultiTenantRequests from '../../../../hooks/query/useMultiTenantRequests'
import LoadingSpinner from '../../../../components/LoadingSpinner'
import type { MultiTenantRequest } from '../../../../types/multiTenantRequest'

const TenantDashboard: FC = () => {
	const navigate = useNavigate()

	const { id: clusterId } = useParams() as {
		id: string
	}

	const { data: tenants, isLoading } = useMultiTenantRequests(clusterId)

	const columnDefinitions = [
		{
			id: 'tenantName',
			header: 'Tenant Name',
			cell: (item: MultiTenantRequest) => (
				<Button
					variant="link"
					onClick={() =>
						navigate(
							`/clusters/${clusterId}/multi-tenant/${item.multiTenantRequestID}`
						)
					}
				>
					{item.details.systemAlias}
				</Button>
			)
		},
		{
			id: 'workloadCluster',
			header: 'Workload Cluster',
			cell: (item: MultiTenantRequest) => item.details.workloadCluster
		},
		{
			id: 'environment',
			header: 'Environment',
			cell: (item: MultiTenantRequest) => item.details.environment
		},
		{
			id: 'dnsZone',
			header: 'DNS Zone',
			cell: (item: MultiTenantRequest) => item.details.dnsZone
		},
		{
			id: 'requestedBy',
			header: 'Requested By',
			cell: (item: MultiTenantRequest) => item.requestorID
		},
		{
			id: 'createdAt',
			header: 'Created At',
			cell: (item: MultiTenantRequest) => item.creationTime
		},
		{
			id: 'status',
			header: 'Status',
			cell: (item: MultiTenantRequest) => {
				switch (item.status) {
					case 'PENDING_APPROVAL':
						return (
							<StatusIndicator type="in-progress" colorOverride="blue">
								Pending Approval
							</StatusIndicator>
						)
					case 'APPROVED':
						return (
							<StatusIndicator type="success">Approved</StatusIndicator>
						)
					case 'REJECTED':
						return (
							<StatusIndicator type="error">Rejected</StatusIndicator>
						)
					default:
						return <Box>-</Box>
				}
			}
		}
	]

	if (isLoading) return <LoadingSpinner />

	return (
		<ContentLayout
			header={
				<Header
					variant="h1"
					description="View tenant onboarding requests for this workload cluster."
				>
					Tenants
				</Header>
			}
		>
			<SpaceBetween direction="vertical" size="l">
				<Container>
					<Table
						columnDefinitions={columnDefinitions}
						items={tenants ?? []}
						empty="No tenant requests found."
					/>
				</Container>

				<Box float="right">
					<Button
						variant="link"
						onClick={() => navigate(`/clusters/${clusterId}`)}
					>
						Back to Cluster Details
					</Button>
				</Box>
			</SpaceBetween>
		</ContentLayout>
	)
}

export default TenantDashboard
